/* eslint-disable no-use-before-define */
import merge from 'lodash/merge';
import { AnyAction } from 'redux';

import { KakaoPixelEventType } from '@app/libs/analytics/kakaoPixel/agent';
import mergeMetaData from '@app/libs/redux/action/mergeMetaData';
import {
  ANALYTICS_DATA_LAYER_VALUES_SET,
  ANALYTICS_DATA_LAYER_VALUES_TRIGGER,
  ANALYTICS_DATA_LAYER_VARIABLES_SET,
  ANALYTICS_EVENT_RECORD,
  ANALYTICS_USER_INITIALIZE,
  ANALYTICS_USER_SET,
} from '@app/store/actionTypes';
import FacebookPixelCustomEventName from '@app/store/analytics/FacebookPixelCustomEventName';
import { ANALYTICS } from '@app/store/metaKeys';

type AnalyticsMetaActions =
  | AnalyticsMetaGAEventRecordAction
  | AnalyticsMetaEventRecordAction
  | AnalyticsMetaUserSetAction
  | AnalyticsMetaDataLayerVariablesSetAction
  | AnalyticsMetaDataLayerValuesSetAction
  | AnalyticsMetaDataLayerValuesTriggerAction
  | AnalyticsMetaUserInitializeAction
  | AnalyticsMetaFacebookPixelEventRecordAction;

interface AnalyticsBaseAction<AnalyticsMetaAction> extends AnyAction {
  meta: {
    [ANALYTICS]: [AnalyticsMetaAction];
  };
}

export interface MultipleAnalyticsAction extends AnyAction {
  meta: {
    [ANALYTICS]: AnalyticsMetaActions[];
  };
}

interface AnalyticsMetaUserInitializeAction extends AnyAction {
  type: typeof ANALYTICS_USER_INITIALIZE;
}

export const initializeUser = (): AnalyticsBaseAction<AnalyticsMetaUserInitializeAction> => ({
  type: ANALYTICS_USER_INITIALIZE,
  meta: {
    [ANALYTICS]: [
      {
        type: ANALYTICS_USER_INITIALIZE,
      },
    ],
  },
});

interface AnalyticsMetaUserSetAction extends AnyAction {
  type: typeof ANALYTICS_USER_SET;
  payload: {
    userId: string;
  };
}

export const setUser = (userId: string): AnalyticsBaseAction<AnalyticsMetaUserSetAction> => ({
  type: ANALYTICS_USER_SET,
  meta: {
    [ANALYTICS]: [
      {
        type: ANALYTICS_USER_SET,
        payload: {
          userId,
        },
      },
    ],
  },
});

interface AnalyticsMetaDataLayerVariablesSetAction extends AnyAction {
  type: typeof ANALYTICS_DATA_LAYER_VARIABLES_SET;
  payload: {
    data: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
}

export const setDataLayerVariables = (
  data: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): AnalyticsBaseAction<AnalyticsMetaDataLayerVariablesSetAction> => ({
  type: ANALYTICS_DATA_LAYER_VARIABLES_SET,
  meta: {
    [ANALYTICS]: [
      {
        type: ANALYTICS_DATA_LAYER_VARIABLES_SET,
        payload: {
          data,
        },
      },
    ],
  },
});

interface AnalyticsMetaDataLayerValuesSetAction extends AnyAction {
  type: typeof ANALYTICS_DATA_LAYER_VALUES_SET;
  payload: {
    eventName: string;
    variables: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
}

export const setDataLayerValues = (
  eventName: string,
  variables: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): AnalyticsBaseAction<AnalyticsMetaDataLayerValuesSetAction> => ({
  type: ANALYTICS_DATA_LAYER_VALUES_SET,
  meta: {
    [ANALYTICS]: [
      {
        type: ANALYTICS_DATA_LAYER_VALUES_SET,
        payload: {
          eventName,
          variables,
        },
      },
    ],
  },
});

interface AnalyticsMetaDataLayerValuesTriggerAction extends AnyAction {
  type: typeof ANALYTICS_DATA_LAYER_VALUES_TRIGGER;
  payload: {
    eventName: string;
  };
}

export const triggerDataLayerValues = (
  eventName: string
): AnalyticsBaseAction<AnalyticsMetaDataLayerValuesTriggerAction> => ({
  type: ANALYTICS_DATA_LAYER_VALUES_TRIGGER,
  meta: {
    [ANALYTICS]: [
      {
        type: ANALYTICS_DATA_LAYER_VALUES_TRIGGER,
        payload: {
          eventName,
        },
      },
    ],
  },
});

interface AnalyticsMetaEventRecordAction extends AnyAction {
  type: typeof ANALYTICS_EVENT_RECORD;
  payload: {
    service: 'facebook' | 'ga' | 'gtm' | 'gtmEcommerce' | 'kakaoPixel';
    [extraProp: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
}

export const recordEvent = (
  service: 'facebook' | 'ga' | 'gtm' | 'gtmEcommerce' | 'kakaoPixel',
  args: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): AnalyticsBaseAction<AnalyticsMetaEventRecordAction> => ({
  type: ANALYTICS_EVENT_RECORD,
  meta: {
    [ANALYTICS]: [
      {
        type: ANALYTICS_EVENT_RECORD,
        payload: {
          service,
          ...args,
        },
      },
    ],
  },
});

interface AnalyticsMetaGAEventRecordAction extends AnyAction {
  type: typeof ANALYTICS_EVENT_RECORD;
  payload: {
    service: 'ga';
    category: string;
    action: string;
    label?: string;
    value?: number;
  };
}

export const recordGAEvent = (category: string, action: string, label?: string, value?: number) =>
  recordEvent('ga', {
    category,
    action,
    label,
    value,
  });

export const recordKakaoMomentEvent = (eventType: KakaoPixelEventType, tag?: string) =>
  recordEvent('kakaoPixel', {
    eventType,
    tag,
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const recordGTMEvent = (eventName: string, variables?: Record<string, any>) =>
  recordEvent('gtm', { eventName, variables });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const recordGTMEcommerceEvent = (eventName: string, variables?: Record<string, any>) =>
  recordEvent('gtmEcommerce', { eventName, variables });

interface AnalyticsMetaFacebookPixelEventRecordAction extends AnyAction {
  type: typeof ANALYTICS_EVENT_RECORD;
  payload: {
    service: 'facebook';
    type: 'track' | 'trackCustom';
    name: string;
    // eslint-disable-next-line @typescript-eslint/ban-types
    parameter?: object;
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const recordPixelStandardEvent = (name: string, parameter?: object) =>
  recordEvent('facebook', {
    type: 'track',
    name,
    parameter,
  });

// eslint-disable-next-line @typescript-eslint/ban-types
export const recordPixelCustomEvent = (name: FacebookPixelCustomEventName, parameter?: object) =>
  recordEvent('facebook', {
    type: 'trackCustom',
    name,
    parameter,
  });

export type AnalyticsAction =
  | ReturnType<typeof initializeUser>
  | ReturnType<typeof setUser>
  | ReturnType<typeof setDataLayerValues>
  | ReturnType<typeof triggerDataLayerValues>
  | ReturnType<typeof recordEvent>
  | ReturnType<typeof recordGAEvent>;

export const withAnalytics = (
  analytics: AnalyticsAction | AnalyticsAction[],
  action: AnyAction
): MultipleAnalyticsAction => {
  if (Array.isArray(analytics)) {
    const metaAnalytics: AnalyticsMetaActions[] = analytics.map((v) => v.meta[ANALYTICS]).flat();
    return merge({}, action, {
      meta: {
        [ANALYTICS]: mergeMetaData(
          {
            type: action.type,
            meta: {
              [ANALYTICS]: metaAnalytics,
            },
          },
          action,
          ANALYTICS
        ),
      },
    });
  }

  return merge({}, action, {
    meta: { [ANALYTICS]: mergeMetaData(analytics, action, ANALYTICS) },
  });
};
